import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { statsAPI } from "@/lib/api";

const COLORS = ["#4F46E5", "#059669", "#D97706", "#DC2626", "#3B82F6", "#8B5CF6"];
const DOC_LABELS = { devis: "Devis", commande: "Commandes", facture: "Factures", avoir: "Avoirs", contrat: "Contrats", proforma: "Devis" };

function formatEur(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

export default function StatsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    statsAPI.get()
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="page-container flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" /></div>;
  }

  const docPieData = Object.entries(data?.document_counts || {}).map(([key, val]) => ({ name: DOC_LABELS[key] || key, value: val })).filter(d => d.value > 0);
  const monthlyData = (data?.monthly_data || []).map(m => ({ ...m, month: new Date(m.month + "-01").toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }) }));
  const topClients = data?.top_clients || [];

  return (
    <div className="page-container" data-testid="stats-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Statistiques</h1>
        <p className="text-slate-500 mt-1">Analyses et tendances de votre activité</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Revenue Evolution */}
        <Card className="p-6 border border-slate-200 rounded-lg bg-white" data-testid="revenue-evolution-chart">
          <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Evolution du chiffre d'affaires</h3>
          {monthlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} tickFormatter={v => `${v}€`} />
                <Tooltip formatter={(v, name) => [name === "revenue" ? formatEur(v) : v, name === "revenue" ? "CA" : name === "devis" ? "Devis" : "Commandes"]} contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#4F46E5" strokeWidth={2} name="Chiffre d'affaires" dot={{ r: 4 }} />
                <Line type="monotone" dataKey="devis" stroke="#D97706" strokeWidth={2} name="Devis" dot={{ r: 3 }} />
                <Line type="monotone" dataKey="commandes" stroke="#059669" strokeWidth={2} name="Commandes" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-slate-400">Pas assez de donnees</div>
          )}
        </Card>

        {/* Document Distribution */}
        <Card className="p-6 border border-slate-200 rounded-lg bg-white" data-testid="doc-distribution-chart">
          <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Repartition des documents</h3>
          {docPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={docPieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {docPieData.map((entry, i) => <Cell key={entry.name || `pie-${i}`} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-slate-400">Aucun document</div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Clients */}
        <Card className="p-6 border border-slate-200 rounded-lg bg-white" data-testid="top-clients-chart">
          <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Top clients par CA</h3>
          {topClients.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topClients} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} tickFormatter={v => `${v}€`} />
                <YAxis dataKey="client" type="category" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} width={120} />
                <Tooltip formatter={(v) => formatEur(v)} contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }} />
                <Bar dataKey="total" fill="#4F46E5" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-slate-400">Aucune donnee</div>
          )}
        </Card>

        {/* Status Breakdown */}
        <Card className="p-6 border border-slate-200 rounded-lg bg-white" data-testid="status-breakdown">
          <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Statuts des documents</h3>
          <div className="space-y-6">
            {Object.entries(data?.status_breakdown || {}).map(([dtype, statuses]) => (
              <div key={dtype}>
                <h4 className="text-sm font-semibold text-slate-700 uppercase mb-2">{DOC_LABELS[dtype] || dtype}</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(statuses).map(([status, count]) => (
                    <div key={status} className={`status-badge status-${status}`}>
                      {status}: {count}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {Object.keys(data?.status_breakdown || {}).length === 0 && (
              <p className="text-slate-400 text-center py-8">Aucune donnee</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
